import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Text,
  Link,
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';
import rollbar from '#services/rollbar';
import { logCheckout } from '#services/checkoutTelemetry';

export default function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const {
    user,
    setLoading,
    setLoadingMessage,
    stripePayments,
    setStripePayments,
    onPaymentSuccess,
    emailError,
    email,
    updateUserPref,
    address,
    sendFBEvent,
    getCheckoutCost,
  } = useUIStore(
    (state) => ({
      user: state.user,
      setLoading: state.setLoading,
      setLoadingMessage: state.setLoadingMessage,
      stripePayments: state.stripePayments,
      setStripePayments: state.setStripePayments,
      onPaymentSuccess: state.onPaymentSuccess,
      emailError: state.emailError,
      email: state.email,
      updateUserPref: state.updateUserPref,
      address: state.address,
      sendFBEvent: state.sendFBEvent,
      getCheckoutCost: state.getCheckoutCost,
    }),
    shallow
  );

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const onSuccess = async ({ subId, paymentId }) => {
    await onPaymentSuccess({ subId, paymentId });
    // Step 4: Remove extra params from the URL
    navigate(`/checkout?success=true&subId=${subId}`);
  };

  async function handlePaymentLifeCycle() {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );
    const subId = new URLSearchParams(window.location.search).get('subId');
    const paymentId = new URLSearchParams(window.location.search).get(
      'paymentId'
    );

    if (!clientSecret) {
      return;
    }

    const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
    switch (paymentIntent.status) {
      case 'succeeded':
        await onSuccess({ subId, paymentId });
        break;
      case 'processing':
        setStripePayments({
          ...stripePayments,
          message: 'Your payment is processing. Please refresh...',
        });
        setMessage('Your payment is processing. Please refresh...');
        break;
      case 'requires_payment_method': {
        const lastError = paymentIntent.last_payment_error;
        const userMessage =
          lastError?.message || 'Your payment was not successful. Please try again.';
        setStripePayments({
          ...stripePayments,
          message: userMessage,
        });
        setMessage(userMessage);
        break;
      }
      default:
        setStripePayments({
          ...stripePayments,
          message: 'Something went wrong',
        });
        setMessage('Something went wrong');
        break;
    }
    setLoading(false);
  }

  useEffect(() => {
    handlePaymentLifeCycle();
  }, [stripe]);

  // Disable the loading screen.
  useEffect(() => {
    if (!elements) {
      setLoading(true);
      logCheckout('info', 'payment step: waiting for Stripe Elements', {
        stripeLoaded: !!stripe,
      });
      return;
    }

    const paymentEl = elements.getElement('payment');
    const mountedAt = Date.now();
    logCheckout('info', 'payment step: Elements available, awaiting PaymentElement ready', {
      stripeLoaded: !!stripe,
      paymentElementFound: !!paymentEl,
    });

    // Watchdog: the loading overlay is only cleared by the PaymentElement
    // 'ready' event below. If that event never fires, the overlay stays up
    // indefinitely (the "stuck on Processing" report). Log it so we can
    // measure how often the element simply never loads. Does NOT clear
    // loading — we are only gathering evidence here.
    const readyWatchdog = setTimeout(() => {
      logCheckout('warning', 'payment step: PaymentElement not ready after 8s', {
        stripeLoaded: !!stripe,
        elementsLoaded: !!elements,
        paymentElementFound: !!paymentEl,
        msWaited: Date.now() - mountedAt,
      });
    }, 8000);

    const onReady = () => {
      clearTimeout(readyWatchdog);
      logCheckout('info', 'payment step: PaymentElement ready', {
        msToReady: Date.now() - mountedAt,
      });
      setTimeout(() => setLoading(false), 200);
    };
    const onLoadError = (event) => {
      clearTimeout(readyWatchdog);
      logCheckout('error', 'payment step: PaymentElement loaderror', {
        errorType: event?.error?.type,
        errorCode: event?.error?.code,
        errorMessage: event?.error?.message,
        msWaited: Date.now() - mountedAt,
      });
    };

    paymentEl?.on('ready', onReady);
    paymentEl?.on('loaderror', onLoadError);

    return () => {
      clearTimeout(readyWatchdog);
      paymentEl?.off?.('ready', onReady);
      paymentEl?.off?.('loaderror', onLoadError);
    };
  }, [elements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null); // Clear any previous error (e.g. after 3DS failure retry)
    logCheckout('info', 'handleSubmit start', {
      stripeLoaded: !!stripe,
      elementsLoaded: !!elements,
      hasEmail: !!user.email,
      emailError: !!emailError,
    });
    if (!user.email && emailError) {
      // Don't proceed if email is invalid
      logCheckout('info', 'submit blocked: email invalid', {});
      setMessage(emailError);
      setLoading(false);
      return;
    }
    setIsLoading(true);

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      // NOTE: this early return leaves the "Processing your payment..." overlay
      // up (set by the pay button's onClick) with no charge attempted — a prime
      // suspect for the "stuck, nothing on Stripe" reports. Logging only.
      logCheckout('warning', 'submit blocked: stripe/elements not ready', {
        stripeLoaded: !!stripe,
        elementsLoaded: !!elements,
      });
      return;
    }
    await updateUserPref({
      email,
      address,
    });
    // Send fb event that user is checking out
    sendFBEvent({ eventName: 'AddPaymentInfo', commerce: 'checkout' });

    const confirmStartedAt = Date.now();
    logCheckout('info', 'confirmPayment start', {});
    let confirmResult;
    try {
      confirmResult = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Make sure to change this to your payment completion page
          return_url: `${process.env.REACT_APP_HOST}/checkout?subId=${stripePayments.subId}&paymentId=${stripePayments.paymentId}`,
        },
        // Let's keep users on the same page if we don't have to redirect.
        redirect: 'if_required',
      });
    } catch (thrown) {
      logCheckout('error', 'confirmPayment threw', {
        message: thrown?.message,
        elapsedMs: Date.now() - confirmStartedAt,
      });
      throw thrown;
    }
    const { error } = confirmResult;
    logCheckout('info', 'confirmPayment settled', {
      elapsedMs: Date.now() - confirmStartedAt,
      hasError: !!error,
      errorType: error?.type,
      errorCode: error?.code,
    });

    if (error) {
      // Log every Stripe confirmPayment error to Rollbar with full context.
      // Only pass serialization-safe fields (no raw error object; it can have nested/circular refs).
      rollbar.error('Stripe confirmPayment failed', {
        errorType: error.type,
        errorCode: error.code,
        errorMessage: error.message,
        errorDeclineCode: error.decline_code,
        errorParam: error.param,
        userId: user?._id,
        userEmail: user?.email || email,
        planSlug: stripePayments.planSlug,
        subId: stripePayments.subId,
        paymentId: stripePayments.paymentId,
        clientSecretExists: !!stripePayments.clientSecret,
        stripeLoaded: !!stripe,
        elementsLoaded: !!elements,
      });

      // Show Stripe's message when present (covers card_error, validation_error,
      // invalid_request_error e.g. payment_intent_authentication_failure).
      setMessage(error.message || 'An unexpected error occurred.');
      setIsLoading(false);
      setLoading(false);
      return;
    }
    // This will be reached when redirect isn't used.
    await onSuccess({
      subId: stripePayments.subId,
      paymentId: stripePayments.paymentId,
    });
    setIsLoading(false);
    setLoading(false);
  };

  const paymentElementOptions = {
    layout: 'tabs',
    wallets: {
      // Not supported from India
      applePay: 'auto',
      googlePay: 'auto',
    },
  };

  const checkoutCost = getCheckoutCost();

  return (
    <Flex direction={'column'} as="h3">
      <Flex mb={2} justifyContent={'space-between'}>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          letterSpacing="wider"
          color="gray.500"
          textTransform="uppercase"
          mb={2}
        >
          Card Details
        </Text>
        {/* <SupportedCards /> */}
      </Flex>
      <form
        id="payment-form"
        onSubmit={handleSubmit}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'stretch'}
      >
        <PaymentElement id="payment-element" options={paymentElementOptions} />
        <button
          id="submit"
          style={{ width: '100%' }}
          onClick={() => {
            logCheckout('info', 'pay button clicked', {
              stripeLoaded: !!stripe,
              elementsLoaded: !!elements,
              isLoading,
            });
            setLoadingMessage('Processing your payment...');
            setLoading(true);
          }}
        >
          <Button
            disabled={isLoading || !stripe || !elements}
            colorScheme="green"
            minW="300px"
            mt={8}
          >
            <span id="button-text">Pay ${checkoutCost} now</span>
          </Button>
        </button>
        {/* Show any error or success messages */}
        {message && (
          <Flex
            id="payment-message"
            mt={4}
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <Alert
              status="error"
              borderRadius="md"
              fontSize="sm"
              py={2}
              px={3}
              w="100%"
            >
              <AlertIcon boxSize="4" />
              <Text as="span">{message}</Text>
            </Alert>
            <Link
              as={RouterLink}
              to="/payment-verification"
              fontSize="sm"
              color="green.600"
              fontWeight="medium"
              _hover={{ textDecoration: 'underline' }}
            >
              Payment issue? Get help →
            </Link>
          </Flex>
        )}
      </form>
    </Flex>
  );
}
